/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState} from 'react'
import {KTIcon, toAbsoluteUrl} from '../../../../_metronic/helpers'
import {ProfileDetails} from './ProfileDetails'
import {useAuth} from '../../../../app/modules/auth'
import {profilePhotoValidationSchema} from '../validate/confirmProfileDetailsSchema'
import Swal from 'sweetalert2'
import {LoadingSpinner} from '../../../../utils/loading/loadingSpinner'
import {createRoot} from 'react-dom/client'
import {postProfilePhoto, deleteProfilePhoto} from '../core/_requests'
import {useNavigate} from 'react-router'
import {SignInMethod} from './SignInMethod'

const PROFILE_PHOTO_URL = process.env.REACT_APP_API_URL + '/api/profile/photo'
const DEFAULT_AVATAR = '/media/svg/avatars/blank.svg'

const ProfileHeader: React.FC = () => {
  const {currentUser} = useAuth()

  const [avatarPreview, setAvatarPreview] = useState<string>(() => {
    return currentUser?.profilePhoto ? PROFILE_PHOTO_URL : toAbsoluteUrl(DEFAULT_AVATAR)
  })

  const navigate = useNavigate()

  const loadingSpinnerSwal = (text: string) => {
    Swal.fire({
      html: '<div id="swal-loading"></div>',
      showConfirmButton: false,
      allowOutsideClick: false,
      didOpen: () => {
        const container = document.getElementById('swal-loading')
        if (container) {
          const root = createRoot(container)
          container.dataset.rootAttached = 'true'
          ;(container as any)._reactRoot = root
          root.render(
            <LoadingSpinner
              title={text}
              style={{
                width: '2rem',
                height: '2rem',
                colorSpinner: '#f1416c',
                colorText: '#11416c',
              }}
            />
          )
        }
      },
      willClose: () => {
        const container = document.getElementById('swal-loading')
        if (container && (container as any)._reactRoot) {
          ;(container as any)._reactRoot.unmount()
          delete (container as any)._reactRoot
        }
      },
    })
  }

  const handleChangeAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const previousPreview = avatarPreview

    try {
      await profilePhotoValidationSchema.validateAt('profilePhoto', {
        profilePhoto: file,
      })

      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string)
      }
      reader.readAsDataURL(file)

      loadingSpinnerSwal('Salvando foto...')

      await postProfilePhoto(file)

      Swal.close()

      await Swal.fire({
        position: 'center',
        icon: 'success',
        title: 'Salvo com sucesso',
        showConfirmButton: false,
        timer: 1500,
      })

      navigate(0)
    } catch (err: any) {
      setAvatarPreview(previousPreview)
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: err.message,
      })
    }

    e.target.value = ''
  }

  const handleRemoveAvatar = async () => {
    await Swal.fire({
      title: 'Deseja remover a foto?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
        cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          loadingSpinnerSwal('Removendo foto...')
          await deleteProfilePhoto()

          const defaultImg = toAbsoluteUrl(DEFAULT_AVATAR)
          setAvatarPreview(defaultImg)

          Swal.close()

          await Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Foto removida com sucesso',
            showConfirmButton: false,
            timer: 1500,
          })

          navigate(0)
        } catch (error: any) {
          Swal.close()
          await Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: error.response?.data?.message,
          })
        }
      }
    })
  }

  return (
    <>
      <div className='card mb-5 mb-xl-10'>
        <div className='card-body pt-9 pb-0'>
          <div className='d-flex flex-wrap flex-sm-nowrap mb-3'>
            <div className='me-7 mb-4'>
              <div className='symbol symbol-100px symbol-lg-175px symbol-fixed position-relative'>
                <div
                  className='image-input image-input-outline'
                  data-kt-image-input='true'
                  style={{backgroundImage: `url(${toAbsoluteUrl(DEFAULT_AVATAR)})`}}
                >
                  <div
                    className='image-input-wrapper w-175px h-175px'
                    style={{
                      backgroundImage: `url(${avatarPreview})`,
                    }}
                  ></div>
                  <label
                    className='btn btn-icon btn-circle btn-active-color-danger w-25px h-25px bg-body shadow'
                    data-kt-image-input-action='change'
                    data-bs-toggle='tooltip'
                    title='Alterar avatar'
                  >
                    <i className='bi bi-pencil-fill fs-7'></i>

                    <input
                      type='file'
                      name='avatar'
                      accept='.png, .jpg, .jpeg'
                      onChange={handleChangeAvatar}
                    />
                    <input type='hidden' name='avatar_remove' />
                  </label>
                  {currentUser?.profilePhoto && (
                    <span
                      className='btn btn-icon btn-circle btn-active-color-danger w-25px h-25px bg-body shadow'
                      data-kt-image-input-action='remove'
                      data-bs-toggle='tooltip'
                      title='Remove avatar'
                      onClick={handleRemoveAvatar}
                    >
                      <i className='bi bi-x fs-2'></i>
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className='flex-grow-1'>
              <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                <div className='d-flex flex-column'>
                  <div className='d-flex align-items-center mb-2'>
                    <a href='#' className='text-gray-800 text-hover-danger fs-2 fw-bolder me-1'>
                      {currentUser?.name}
                    </a>
                    <a href='#'>
                      <KTIcon iconName='verify' className='fs-1 text-danger' />
                    </a>
                  </div>

                  <div className='d-flex flex-wrap fw-bold fs-6 mb-4 pe-2'>
                    <span className='d-flex align-items-center text-gray-400 text-hover-danger me-5 mb-2'>
                      <KTIcon iconName='profile-circle' className='fs-4 me-1' />
                      {currentUser?.role}
                    </span>
                    <span className='d-flex align-items-center text-gray-400 text-hover-danger mb-2'>
                      <KTIcon iconName='sms' className='fs-4 me-1' />
                      {currentUser?.email}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='d-flex overflow-auto h-55px'>
            <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder flex-nowrap'>
              <li className='nav-item'>
                <button
                  className='nav-link active'
                  id='pills-data-tab'
                  data-bs-toggle='pill'
                  data-bs-target='#pills-data'
                  type='button'
                  role='tab'
                  aria-controls='pills-data'
                  aria-selected='true'
                >
                  Dados
                </button>
              </li>
              <li className='nav-item'>
                <button
                  className='nav-link'
                  id='pills-change-tab'
                  data-bs-toggle='pill'
                  data-bs-target='#pills-change'
                  type='button'
                  role='tab'
                  aria-controls='pills-change'
                  aria-selected='true'
                >
                  Alterar Senha / E-mail
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className='tab-content' id='pills-tabContent'>
        <div
          className='tab-pane fade show active p-4 pt-0 pt-md-4'
          id='pills-data'
          role='tabpanel'
          aria-labelledby='pills-checklist-tab'
        >
          <ProfileDetails
            userProfile={{
              name: currentUser?.name || '',
              cpf: currentUser?.cpf || '',
              phone: currentUser?.phone || '',
              notification_preference: currentUser?.notificationPreference || 0,
            }}
          />
        </div>
        <div
          className='tab-pane fade p-4 pt-0 pt-md-4'
          id='pills-change'
          role='tabpanel'
          aria-labelledby='pills-checklist-tab'
        >
          <SignInMethod email={currentUser?.email || ''} />
        </div>
      </div>
    </>
  )
}

export {ProfileHeader}
